import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Search, Type, User, LogOut, LayoutDashboard, Shield } from "lucide-react";
import api from "@/lib/api";

const NAV = [
  { to: "/classes", label: "Classes" },
  { to: "/events", label: "Events" },
  { to: "/instructors", label: "Instructors" },
  { to: "/new-dancers", label: "New Dancers" },
  { to: "/about", label: "About Us" },
];

export function Header() {
  const { user, logout, isStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dyslexia, setDyslexia] = useState(() => localStorage.getItem("dyslexia") === "1");
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => { setOpen(false); setSearchOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.documentElement.classList.toggle("dyslexia-mode", dyslexia);
    localStorage.setItem("dyslexia", dyslexia ? "1" : "0");
  }, [dyslexia]);

  useEffect(() => {
    if (!searchOpen || q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, searchOpen]);

  const go = (path) => { navigate(path); setSearchOpen(false); setQ(""); };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-[#FAF7F2]/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Lawley Village Line Dance Club" className="h-14 w-auto sm:h-16" />
          <span className="sr-only">Lawley Village Line Dance Club</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-[#5C4A42] hover:bg-secondary hover:text-[#B04B26]">{n.label}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <button onClick={() => setSearchOpen(true)} data-testid="open-search" aria-label="Search"
            className="rounded-lg p-2.5 text-[#5C4A42] hover:bg-secondary"><Search size={20} /></button>
          <button onClick={() => setDyslexia((v) => !v)} data-testid="dyslexia-toggle" aria-pressed={dyslexia}
            aria-label="Toggle dyslexia-friendly font" title="Dyslexia-friendly font"
            className={`rounded-lg p-2.5 hover:bg-secondary ${dyslexia ? "bg-[#FBEBE4] text-[#B04B26]" : "text-[#5C4A42]"}`}><Type size={20} /></button>

          {user === undefined ? null : user ? (
            <div className="hidden items-center gap-1.5 sm:flex">
              <Link to="/dashboard" data-testid="nav-dashboard" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-[#5C4A42] hover:bg-secondary hover:text-[#B04B26]"><LayoutDashboard size={16} /> Dashboard</Link>
              {isStaff && <Link to="/admin" data-testid="nav-admin" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-[#4A6B5D] hover:bg-secondary"><Shield size={16} /> Admin</Link>}
              <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-button" aria-label="Log out" className="rounded-lg p-2.5 text-[#5C4A42] hover:bg-secondary"><LogOut size={18} /></button>
            </div>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Link to="/login" data-testid="nav-login" className="rounded-lg px-3 py-2 text-sm font-semibold text-[#5C4A42] hover:text-[#B04B26]">Log in</Link>
              <Link to="/register" data-testid="nav-register" className="rounded-full bg-[#C85A32] px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-[#B04B26]">Join the club</Link>
            </div>
          )}

          <button onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu" className="rounded-lg p-2.5 text-[#5C4A42] hover:bg-secondary lg:hidden">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-[#FAF7F2] lg:hidden" data-testid="mobile-menu">
          <div className="space-y-1 px-4 py-3">
            {NAV.map((n) => <Link key={n.to} to={n.to} className="block rounded-lg px-3 py-2.5 font-semibold text-[#2D1E18] hover:bg-secondary">{n.label}</Link>)}
            <div className="my-2 h-px bg-border" />
            {user ? (
              <>
                <Link to="/dashboard" className="block rounded-lg px-3 py-2.5 font-semibold text-[#2D1E18] hover:bg-secondary">Dashboard</Link>
                {isStaff && <Link to="/admin" className="block rounded-lg px-3 py-2.5 font-semibold text-[#4A6B5D] hover:bg-secondary">Admin area</Link>}
                <button onClick={() => { logout(); navigate("/"); }} className="block w-full rounded-lg px-3 py-2.5 text-left font-semibold text-[#2D1E18] hover:bg-secondary">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="block rounded-lg px-3 py-2.5 font-semibold text-[#2D1E18] hover:bg-secondary">Log in</Link>
                <Link to="/register" className="block rounded-lg bg-[#C85A32] px-3 py-2.5 text-center font-bold text-white">Join the club</Link>
              </>
            )}
          </div>
        </div>
      )}

      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-20" onClick={() => setSearchOpen(false)} data-testid="search-overlay">
          <div className="w-full max-w-xl rounded-2xl bg-card p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 rounded-xl border border-border px-3">
              <Search size={18} className="text-[#6E5B51]" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
                placeholder="Search dances, classes, events…" className="w-full bg-transparent py-3 text-[#2D1E18] outline-none" />
            </div>
            {results && (
              <div className="mt-3 max-h-80 overflow-y-auto">
                {["dances", "classes", "events"].map((k) => results[k]?.length ? (
                  <div key={k} className="mb-3">
                    <p className="px-2 py-1 text-xs font-bold uppercase tracking-wider text-[#6E5B51]">{k}</p>
                    {results[k].map((r, i) => (
                      <button key={i} data-testid={`search-result-${k}-${i}`} onClick={() => go(k === "dances" ? `/dances/${r.slug}` : k === "classes" ? `/classes/${r.slug}` : `/events/${r.slug}`)}
                        className="block w-full rounded-lg px-2 py-2 text-left hover:bg-secondary">
                        <span className="font-semibold text-[#2D1E18]">{r.name || r.title}</span>
                        <span className="ml-2 text-xs text-[#6E5B51]">{r.level || r.day || r.date}</span>
                      </button>
                    ))}
                  </div>
                ) : null)}
                {!results.dances.length && !results.classes.length && !results.events.length && (
                  <p className="px-2 py-4 text-center text-sm text-[#6E5B51]">No results for “{q}”.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
